import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, MessageCircle, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { TESTIMONIALS_WEBHOOK_URL } from '../config/webhooks';

const WA_URL  = 'https://wa.me/919246887272?text=Hi%2C+I+am+interested+in+booking+a+school+tour';
const WA_SHARE = 'https://wa.me/919246887272?text=Hello!+I%E2%80%99d+like+to+share+my+experience+as+an+Aurobindo+parent.';

const themes = [
  { quote: '"My child has become genuinely independent — she manages herself, even through the holidays. She finds her own work and gets busy."' },
  { quote: '"His spoken English has improved dramatically — and he\'ll speak in front of anyone, without fear."' },
  { quote: '"Aurobindo children are just… different. The behaviour, the attention span, the way they get along with other children."' },
  { quote: '"Not a single regret."' },
];

const testimonials = [
  {
    id: 'megha',
    name: 'Ms. Megha',
    role: 'Mother of Dwisha · Primary',
    initials: 'ME',
    pull: 'The difference in her focus and patience is remarkable compared to my other daughters at the same age in a CBSE setup.',
    full: 'As a mother of three, I\'ve had the unique opportunity to compare parenting journeys across two very different educational philosophies. My youngest daughter has been at Aurobindo Montessori since she turned 4, and the difference in her focus and patience is remarkable compared to my other daughters at the same age in a CBSE setup. She sits with an activity — sorting, puzzles, practical life tasks — and stays with it until she\'s done, without needing constant redirection. It\'s clear the Montessori method of self-paced, hands-on learning is building a foundation of concentration that I didn\'t see develop as early in my other children.',
  },
  {
    id: 'payal',
    name: 'Ms. Payal',
    role: 'Mother of Mohak Pala · Primary',
    initials: 'PA',
    pull: 'He folds his clothes, eats on his own, arranges the table for everyone — and his knowledge of planets, continents and oceans is incredible for his age.',
    full: 'Our journey with Sri Aurobindo Montessori has been wonderful. Our son, Mohak, truly finds the comfort of home in his school and he remains so cheerful every single day. He has become more patient and has developed a real sense of responsibility. He doesn\'t think twice before helping with anything around the house. We love seeing how organized he\'s become — he folds his clothes and puts them away in his cupboard, eats on his own and always keeps his plate. He even arranges the table for everyone during meal times and thoughtfully sets the bed for everyone at night. At the same time, his spoken English has polished up beautifully and his knowledge about things like planets, continents, oceans and states is incredible for his age. We also love seeing how he is getting closer to his culture as he grows. The school has done a fantastic job of encouraging his curiosity while teaching him structure and independence. We are so grateful for the foundation you are giving him.',
  },
  {
    id: 'ashish',
    name: 'Mr. Ashish Samat',
    role: 'Father of Anvi & Anika · Primary',
    initials: 'AS',
    pull: 'Our children wake up excited every morning because they genuinely love going to school — a feeling every parent hopes for.',
    full: 'Choosing this school for our daughters has been one of the best decisions we have made. We truly feel fortunate that they are part of such an authentic Montessori environment. For over 25 years, this school, under the able guidance of Sujatha Ma\'am, has remained committed to the true Montessori philosophy. Her vision, dedication, and passion for nurturing young minds are reflected in every aspect of the school. The Montessori principles are followed with sincerity and discipline, creating a space where children learn with joy, confidence, independence, and curiosity. The positive, calm, and nurturing atmosphere is something truly special. Our children wake up excited every morning because they genuinely love going to school — a feeling every parent hopes for. The teachers are caring, patient, and deeply committed to every child\'s growth. The love, values, and confidence our daughters have developed here are a testament to the school\'s exceptional environment and unwavering commitment to authentic Montessori education. We are deeply grateful to Sujatha Ma\'am and the entire school team for giving our children such a joyful beginning to their learning journey. We wholeheartedly recommend this school to every parent seeking a true Montessori education in a loving, inspiring, and positive environment.',
  },
  {
    id: 'manasa',
    name: 'Ms. Manasa',
    role: 'Mother of Drishti · Elementary',
    initials: 'MA',
    pull: 'The Montessori materials helped her develop an intuitive understanding of concepts, particularly in mathematics, rather than relying only on memorisation.',
    full: 'Our daughter, Drishti, has been part of Sri Aurobindo Montessori House of Children since she was 2 years and 8 months old, and she is now 8. She began her journey in Primary and later moved to Elementary. Over the years, we have seen her grow into a confident, independent, and articulate child. Her academic and communication skills have developed steadily, and she has become increasingly comfortable expressing her ideas, asking questions, and taking responsibility for her work. The Montessori materials have played an especially important role in her learning. Working with them has helped her develop an intuitive understanding of underlying concepts, particularly in mathematics, rather than relying only on memorisation. We are deeply grateful to her guides for the warmth, patience, and thoughtful guidance they have provided, and for helping Drishti build such a strong and joyful foundation.',
  },
  {
    id: 'aditya',
    name: 'Mr. Aditya',
    role: 'Father of Aadhya Vuppala · Alumni 2017–2025',
    initials: 'AD',
    pull: 'The Montessori environment nurtured her curiosity, encouraged independent thinking, and helped her develop into a confident, compassionate, responsible young individual.',
    full: 'From the moment Aadhya joined Aurobindo Montessori School in 2017 until she graduated from the Montessori programme in 2025, she experienced a remarkable journey of learning and self-discovery. The Montessori environment nurtured her natural curiosity, encouraged independent thinking, instilled self-discipline, and helped her develop into a confident, compassionate, and responsible young individual. The school\'s emphasis on holistic development is reflected in Aadhya\'s achievements beyond academics. She has earned multiple Gold, Silver, and Bronze medals in gymnastics at district, state, and national-level competitions, and has distinguished herself in karate by winning medals at state championships. These accomplishments are a testament to the values and confidence cultivated during her Montessori years. As Aadhya embarks on the next chapter of her educational journey, she carries with her the enduring values and lifelong lessons imparted by Aurobindo Montessori School — an institution that has played a significant role in shaping her character, confidence, and aspirations.',
  },
  {
    id: 'sravani',
    name: 'Ms. Sravani',
    role: 'Mother of Harini · Primary',
    initials: 'SR',
    pull: 'Wonderful to see her grow into an independent, confident, and curious child — she even makes dosas under our supervision at five.',
    full: 'My daughter joined this school when she was just 2½ years old, and today, at the age of 5, it has been wonderful to see her grow into an independent, confident, and curious child. The Montessori approach has played a significant role in shaping her everyday habits. She willingly takes responsibility for small tasks at home, folds and arranges her clothes neatly in her cupboard, and enjoys helping with household activities. She often asks if she can help with cooking and can make simple items like dosas under our supervision. We believe the Montessori environment has given her the confidence to explore, learn, and contribute independently. As parents, we value that the school focuses not only on academics but also on nurturing life skills, responsibility, self-discipline, and confidence. These are qualities that will stay with children long after they leave the classroom.',
  },
];

function TestimonialCard({ t, index }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      data-testid={`testimonial-card-${t.id}`}
      className="bg-white rounded-3xl p-8 shadow-[0_4px_24px_rgba(59,130,246,0.07)] hover:shadow-[0_12px_40px_rgba(59,130,246,0.12)] transition-all duration-300 flex flex-col border border-blue-50"
    >
      {/* Large number */}
      <p className="text-xs font-bold text-[#BFDBFE] mb-4 uppercase tracking-widest">
        {String(index + 1).padStart(2, '0')}
      </p>

      {/* Pull quote */}
      <p className="text-[#1E3A8A] font-black text-lg sm:text-xl leading-snug mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
        "{t.pull}"
      </p>

      {/* Expanded text */}
      {expanded && (
        <p className="text-slate-500 text-sm leading-relaxed mb-4 italic border-t border-blue-50 pt-4">
          {t.full}
        </p>
      )}

      {/* Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        data-testid={`toggle-${t.id}`}
        className="text-[#60A5FA] text-xs font-bold flex items-center gap-1 hover:text-[#1E3A8A] transition-colors mt-auto mb-5"
      >
        {expanded ? <><ChevronUp size={14} /> Show less</> : <><ChevronDown size={14} /> Read full</>}
      </button>

      {/* Attribution */}
      <div className="flex items-center gap-3 pt-4 border-t border-blue-50">
        <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0">
          <span className="text-[#1E3A8A] font-black text-xs">{t.initials}</span>
        </div>
        <div>
          <p className="font-bold text-[#0F172A] text-sm">{t.name}</p>
          <p className="text-slate-400 text-xs">{t.role}</p>
        </div>
      </div>
    </div>
  );
}

const initialForm = { name: '', mobile: '', childInfo: '', words: '', consent: false, credit: 'full', includeChild: false };

export default function Testimonials() {
  const [form, setForm]             = useState(initialForm);
  const [submitted, setSubmitted]   = useState(false);
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const creditLabel = { full: 'Full name', first: 'First name only', anon: 'An Aurobindo parent' };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim())  errs.name  = 'Your name is required';
    if (!form.words.trim()) errs.words = 'Please share a few words';
    if (!form.consent)      errs.consent = 'Please tick the box to allow us to use your feedback';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSubmitting(true);
    setSubmitError('');
    try {
      if (TESTIMONIALS_WEBHOOK_URL) {
        const payload = {
          source:           'Parents',
          name:             form.name,
          mobile:           form.mobile,
          association:      form.childInfo,
          testimonial:      form.words,
          consentToPublish: form.consent ? 'Yes' : 'No',
          creditPreference: creditLabel[form.credit] || form.credit,
          includeChildName: form.includeChild ? 'Yes' : 'No',
        };
        await fetch(TESTIMONIALS_WEBHOOK_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
          mode:    'no-cors',
        });
      }
      setSubmitted(true);
    } catch (_) {
      setSubmitError('Something went wrong — please try again, or WhatsApp us at +91\u00a092468\u00a087272.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16">
      <Helmet>
        <title>Parent Testimonials | Sri Aurobindo Montessori House of Children, Hyderabad</title>
        <meta name="description" content="For 25 years, every family at our Montessori school in West Marredpally, Secunderabad has come through another parent's recommendation. Read what parents say about their children's growth." />
      </Helmet>

      {/* ── BANNER ── */}
      <section className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">25 Years · Zero Ads</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-5"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="testimonials-h1"
          >
            Every family here was<br className="hidden sm:block" /> sent by another family.
          </h1>
          <p className="text-blue-200 text-base max-w-2xl mx-auto">
            In 25 years we have never advertised — no pamphlets, no hoardings, no paid promotions. Our admissions have always run on a single engine: happy parents telling other parents. This page is that engine, in writing.
          </p>
        </div>
      </section>

      {/* ── WHAT PARENTS NOTICE ── */}
      <section className="py-16 bg-white" data-testid="themes-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Recurring Themes</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
            What parents notice
          </h2>
          <p className="text-slate-500 text-sm mb-8">
            Across 1200+ children, the feedback repeats itself with remarkable consistency. These are paraphrased themes from what parents tell us — their own words appear below.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {themes.map((t, i) => (
              <div
                key={i}
                data-testid={`theme-${i}`}
                className="bg-[#F4F7FB] rounded-2xl px-6 py-5 border-l-4 border-[#BFDBFE] hover:border-[#1E3A8A] transition-colors duration-200"
              >
                <p className="text-[#1E3A8A] font-semibold text-sm leading-relaxed italic">{t.quote}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IN PARENTS' OWN WORDS ── */}
      <section className="py-16 bg-[#F4F7FB]" data-testid="cards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Attributed</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A]" style={{ fontFamily: 'Nunito, sans-serif' }}>
              In parents' own words
            </h2>
            <p className="text-slate-400 text-sm mt-2">Tap "Read full" on any card to see the complete story.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <TestimonialCard key={t.id} t={t} index={i} />
            ))}

            {/* Pending slot */}
            <div
              data-testid="testimonial-pending"
              className="bg-[#EFF6FF] rounded-3xl p-8 border-2 border-dashed border-[#BFDBFE] flex flex-col items-center justify-center text-center min-h-[200px]"
            >
              <p className="text-[#93C5FD] font-bold text-xs uppercase tracking-wide">More Stories Coming</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── ADD YOUR VOICE ── */}
      <section className="py-16 bg-white" data-testid="contribute-section">
        <div className="max-w-xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">For Past & Present Parents</p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Add your voice
            </h2>
            <p className="text-slate-500 text-sm leading-relaxed max-w-md mx-auto">
              Are you an Aurobindo parent, past or present? A few honest sentences from you are the greatest gift you can give the next nervous parent — the one standing exactly where you once stood, wondering whether Montessori is the right choice for their child.
            </p>
            <p className="text-[#1E3A8A] font-bold text-sm mt-3">It takes two minutes.</p>
          </div>

          {submitted ? (
            <div className="bg-[#EFF6FF] rounded-3xl p-10 text-center" data-testid="contribute-success">
              <p className="text-3xl mb-4">&#127800;</p>
              <h3 className="text-xl font-extrabold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Thank you — truly.
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Your words may be the reassurance another parent needs.
              </p>
              <a
                href={WA_SHARE}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 text-sm font-bold hover:bg-[#128C7E] transition-colors"
              >
                <MessageCircle size={14} /> Or share on WhatsApp
              </a>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="contribute-form"
              className="bg-white rounded-3xl p-8 shadow-[0_4px_30px_rgba(59,130,246,0.08)] border border-blue-50 space-y-5"
              noValidate
            >
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-[#1E3A8A] mb-1.5">Your name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="e.g. Megha Sharma"
                  data-testid="input-contrib-name"
                  className="w-full rounded-2xl border-2 border-blue-100 bg-[#F4F7FB] px-5 py-3.5 text-slate-800 placeholder-slate-400 focus:border-[#60A5FA] focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all text-sm"
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Mobile */}
              <div>
                <label className="block text-sm font-semibold text-[#1E3A8A] mb-1.5">
                  Mobile number <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  placeholder="We'll only use this if we need to reach you (not published)"
                  data-testid="input-contrib-mobile"
                  className="w-full rounded-2xl border-2 border-blue-100 bg-[#F4F7FB] px-5 py-3.5 text-slate-800 placeholder-slate-400 focus:border-[#60A5FA] focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all text-sm"
                />
              </div>

              {/* Child info */}
              <div>
                <label className="block text-sm font-semibold text-[#1E3A8A] mb-1.5">
                  Your child at Aurobindo <span className="text-slate-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  name="childInfo"
                  value={form.childInfo}
                  onChange={handleChange}
                  placeholder="e.g. Primary, 2019 to 2023"
                  data-testid="input-contrib-child"
                  className="w-full rounded-2xl border-2 border-blue-100 bg-[#F4F7FB] px-5 py-3.5 text-slate-800 placeholder-slate-400 focus:border-[#60A5FA] focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all text-sm"
                />
                <p className="text-slate-400 text-xs mt-1">Just the program and rough years is perfect.</p>
              </div>

              {/* Words */}
              <div>
                <label className="block text-sm font-semibold text-[#1E3A8A] mb-1.5">
                  In your words, what would you tell a nervous parent?
                </label>
                <textarea
                  name="words"
                  value={form.words}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Speak from the heart — a few honest sentences mean more than anything polished."
                  data-testid="input-contrib-words"
                  className="w-full rounded-2xl border-2 border-blue-100 bg-[#F4F7FB] px-5 py-3.5 text-slate-800 placeholder-slate-400 focus:border-[#60A5FA] focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all text-sm resize-none"
                />
                {errors.words && <p className="text-red-500 text-xs mt-1">{errors.words}</p>}
              </div>

              {/* Consent & credit block */}
              <div className="bg-[#F4F7FB] rounded-2xl px-6 py-5 space-y-4">

                {/* Publish consent */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={handleChange}
                    data-testid="input-contrib-consent"
                    className="mt-0.5 accent-[#1E3A8A] w-4 h-4 flex-shrink-0"
                  />
                  <span className="text-slate-600 text-xs leading-relaxed">
                    I'm happy for the school to share my words on its website and social media.{' '}
                    <span className="text-slate-400">(Required to publish — leave unticked and we simply keep your feedback privately, with thanks.)</span>
                  </span>
                </label>
                {errors.consent && <p className="text-red-500 text-xs">{errors.consent}</p>}

                {/* Credit radio */}
                <div>
                  <p className="text-slate-600 text-xs font-semibold mb-2">How should we credit you?</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {[
                      { value: 'full', label: 'Full name' },
                      { value: 'first', label: 'First name only' },
                      { value: 'anon', label: 'Just "an Aurobindo parent"' },
                    ].map(opt => (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="credit"
                          value={opt.value}
                          checked={form.credit === opt.value}
                          onChange={handleChange}
                          data-testid={`input-credit-${opt.value}`}
                          className="accent-[#1E3A8A] w-4 h-4"
                        />
                        <span className="text-slate-600 text-xs">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Child name opt-in */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="includeChild"
                    checked={form.includeChild}
                    onChange={handleChange}
                    data-testid="input-contrib-include-child"
                    className="mt-0.5 accent-[#1E3A8A] w-4 h-4 flex-shrink-0"
                  />
                  <span className="text-slate-500 text-xs leading-relaxed">
                    You may include my child's first name.{' '}
                    <span className="text-slate-400">(Leave unticked and we'll refer only to the program, e.g. "parent of a Primary child".)</span>
                  </span>
                </label>
              </div>

              {/* Network error banner */}
              {submitError && (
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3" data-testid="testimonials-submit-error">
                  <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-800 text-sm leading-relaxed">{submitError}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="contribute-submit-btn"
                className="w-full bg-[#1E3A8A] text-white rounded-full py-4 font-black text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Sending…' : 'Share My Experience'}
              </button>
            </form>
          )}

          {/* WhatsApp alternative */}
          <div className="mt-10 text-center">
            <p className="text-slate-500 text-sm mb-2 font-semibold">Prefer to just talk, or send a quick voice note?</p>
            <p className="text-slate-400 text-xs mb-4 leading-relaxed">
              Some things are easier said than typed. Message us on WhatsApp and share your experience, whichever way it feels natural — sharing a voice note or texting a few lines.
            </p>
            <a
              href={WA_SHARE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contribute-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-7 py-3.5 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
            >
              <MessageCircle size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>WhatsApp</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ── BECOME ONE OF THESE PARENTS ── */}
      <section className="py-16 bg-[#1E3A8A]" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Your Turn</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-8" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Become one of these parents
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/admissions#enquiry-form"
              data-testid="testimonials-tour-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Book a Tour <ArrowRight size={16} />
            </Link>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="testimonials-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} />
              <span className="flex flex-col items-start leading-tight">
                <span>WhatsApp</span>
                <span className="font-normal opacity-90" style={{ fontSize: '0.7rem' }}>+91 92468 87272</span>
              </span>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
